import { useCallback, useRef, useState } from 'react';
import {
  HttpError,
  useCreate,
  useDataProvider,
  useNotify,
} from 'react-admin';

import resourceNames from '../constants/resourceNames';
import { refreshAuth } from '../utilities/Authprovider';
import errorNotification from '../utilities/errorNotification';
import { pickStartingPrompts, StartingPromptDraw } from '../components/pingu/startingPrompts';
import {
  ChatExchange,
  ChatExchangeInput,
  ChatMessage,
  ChatMessageFeedbackInput,
  ChatSessionDetail,
  CostBreakdown,
} from '../components/pingu/types';

const toCostBreakdown = (source: CostBreakdown): CostBreakdown => ({
  totalInputTokens: source.totalInputTokens,
  totalOutputTokens: source.totalOutputTokens,
  totalCostUsd: source.totalCostUsd,
  totalMainCostUsd: source.totalMainCostUsd,
  totalValidatorCostUsd: source.totalValidatorCostUsd,
});

const withEmptyReplyPlaceholder = (messages: ChatMessage[]): ChatMessage[] => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || lastMessage.role !== 'user') return messages;

  return [...messages, {
    messageIndex: -1,
    role: 'assistant',
    content: '',
    emptyReply: true,
  }];
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 900;
// Pingu reuses the sent access token for every API call during a reply that can run for minutes.
const PINGU_TOKEN_MIN_VALIDITY_MS = 10 * 60 * 1000;

type ChatSendOptions = {
  pageContext?: string;
  formId?: string;
};

const wait = (delayMs: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, delayMs);
});

type EncodedChatAttachment = { name: string; mediaType: string; data: string };

const encodeFile = async (file: File): Promise<EncodedChatAttachment> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const chunks: string[] = [];
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + chunkSize)));
  }
  return { name: file.name, mediaType: file.type, data: btoa(chunks.join('')) };
};

// Owns one conversation: its transcript, which stored session it belongs to and
// what it has cost so far. Lives above the panel so closing the panel does not
// discard the exchange the member is in the middle of.
const usePinguChat = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [sendChatMessage] = useCreate<ChatExchangeInput, HttpError, ChatExchange>();
  const lastSendOptions = useRef<ChatSendOptions>({});
  const lastSentFiles = useRef<File[]>([]);
  const pollingGenerations = useRef(new Map<string, number>());
  const activeSessionId = useRef<string | null>(null);
  const [sessionProcessingStatuses, setSessionProcessingStatuses] = useState<Map<string, boolean>>(
    new Map(),
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionName, setSessionName] = useState('');
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [startingPrompts, setStartingPrompts] = useState<StartingPromptDraw>(
    () => pickStartingPrompts(Math.random),
  );

  const setSessionProcessing = useCallback((selectedSessionId: string, processing: boolean) => {
    setSessionProcessingStatuses((previous) => {
      const next = new Map(previous);
      next.set(selectedSessionId, processing);
      return next;
    });
  }, []);

  const startPollingGeneration = useCallback((selectedSessionId: string): number => {
    const generation = (pollingGenerations.current.get(selectedSessionId) || 0) + 1;
    pollingGenerations.current.set(selectedSessionId, generation);
    return generation;
  }, []);

  const applySession = useCallback((
    session: ChatSessionDetail,
    markLatestAssistantAsNew = false,
  ) => {
    const latestAssistantIndex = session.messages?.findLastIndex(({ role }) => role === 'assistant') ?? -1;

    activeSessionId.current = session.id;
    setSessionId(session.id);
    setSessionName(session.name || '');
    const storedMessages = (session.messages || []).map((message, index) => ({
      ...message,
      justReceived: markLatestAssistantAsNew && index === latestAssistantIndex,
    }));
    setMessages(session.processing || session.cancelled
      ? storedMessages
      : withEmptyReplyPlaceholder(storedMessages));
    setCostBreakdown(toCostBreakdown(session));
    setSessionProcessing(session.id, session.processing);
    setLoadingSession(false);
    setLoading(session.processing);
  }, [setSessionProcessing]);

  const pollSessionUntilDone = useCallback(async (
    selectedSessionId: string,
    generation: number,
    markLatestAssistantAsNew = false,
  ): Promise<boolean> => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      // Polling has to happen one attempt at a time, so the sequential await is intentional.
      // eslint-disable-next-line no-await-in-loop
      await wait(POLL_INTERVAL_MS);
      if (pollingGenerations.current.get(selectedSessionId) !== generation) return false;

      // eslint-disable-next-line no-await-in-loop
      const { data } = await dataProvider.getOne<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        { id: selectedSessionId },
      );
      if (data.id === activeSessionId.current) {
        applySession(data, markLatestAssistantAsNew && !data.processing);
      } else setSessionProcessing(data.id, data.processing);
      if (!data.processing) return true;
    }

    setSessionProcessing(selectedSessionId, false);
    if (selectedSessionId === activeSessionId.current) setLoading(false);
    notify('Pingwin is still working. Reopen this conversation in a moment to see the answer.', { type: 'info' });
    return false;
  }, [applySession, dataProvider, notify, setSessionProcessing]);

  // Retiring the flag once written out survives closing and reopening the panel,
  // which unmounts the whole transcript and would otherwise replay every reply.
  const markMessageTyped = useCallback((index: number) => {
    setMessages((previous) => (previous[index]?.justReceived
      ? previous.map((message, messageIndex) => (
        messageIndex === index ? { ...message, justReceived: false } : message
      ))
      : previous));
  }, []);

  const startNewSession = useCallback(() => {
    activeSessionId.current = null;
    lastSendOptions.current = {};
    lastSentFiles.current = [];
    setSessionId(null);
    setSessionName('');
    setMessages([]);
    setCostBreakdown(null);
    setLoadingSession(false);
    setLoading(false);
    setStartingPrompts(pickStartingPrompts(Math.random));
  }, []);

  const loadSession = useCallback(async (selectedSessionId: string): Promise<boolean> => {
    setLoadingSession(true);
    setLoading(true);
    try {
      const { data } = await dataProvider.getOne<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        { id: selectedSessionId },
      );

      const generation = startPollingGeneration(data.id);
      // A stored transcript is history, so it is shown complete rather than typed.
      applySession(data);
      if (data.processing) {
        setSessionProcessing(data.id, true);
        pollSessionUntilDone(data.id, generation);
      }
      return true;
    } catch (e) {
      setLoadingSession(false);
      setLoading(false);
      notify(errorNotification(e, 'Conversation could not be loaded'), { type: 'error' });
      return false;
    }
  }, [
    applySession,
    dataProvider,
    notify,
    pollSessionUntilDone,
    setSessionProcessing,
    startPollingGeneration,
  ]);

  const sendMessage = useCallback(async (
    text: string,
    options: ChatSendOptions = {},
    files: File[] = [],
  ) => {
    if ((!text.trim() && files.length === 0) || loading) return;
    lastSendOptions.current = options;
    lastSentFiles.current = files;

    setMessages((previous) => [...previous, {
      messageIndex: -1,
      role: 'user',
      content: text,
      attachments: files.map(({ name, type }) => ({ name, mediaType: type })),
    }]);
    setLoading(true);

    let exchange: ChatExchange | void;
    try {
      await refreshAuth(PINGU_TOKEN_MIN_VALIDITY_MS);
      exchange = await sendChatMessage(
        options.formId
          ? `${resourceNames.FORMS}/${options.formId}/studio/chat`
          : resourceNames.PINGU_CHAT,
        {
          data: {
            message: text,
            ...(sessionId ? { sessionId } : {}),
            ...(!options.formId && options.pageContext ? { pageContext: options.pageContext } : {}),
            ...(files.length ? { attachments: await Promise.all(files.map(encodeFile)) } : {}),
          },
        },
        { returnPromise: true },
      );
    } catch (e) {
      setMessages((previous) => [...previous, {
        messageIndex: -1,
        role: 'assistant',
        content: "Pingwin couldn't be reached, so this question wasn't answered.",
        error: true,
      }]);
      setLoading(false);
      return;
    }

    try {
      if (exchange?.id) {
        activeSessionId.current = exchange.id;
        setSessionId(exchange.id);
        setSessionName(exchange.name || '');
        setSessionProcessing(exchange.id, Boolean(exchange.processing));
      }
      if (exchange) setCostBreakdown(toCostBreakdown(exchange));
      if (exchange?.processing && exchange.id) {
        const generation = startPollingGeneration(exchange.id);
        await pollSessionUntilDone(exchange.id, generation, true);
        return;
      }
      setMessages((previous) => [...previous, (exchange?.reply
        ? {
          messageIndex: -1,
          role: 'assistant' as const,
          content: exchange.reply,
          justReceived: true,
        }
        : {
          messageIndex: -1,
          role: 'assistant' as const,
          content: '',
          emptyReply: true,
        })]);
      setLoading(false);
    } catch (e) {
      if (exchange?.id && exchange.id !== activeSessionId.current) return;
      setMessages((previous) => [...previous, {
        messageIndex: -1,
        role: 'assistant',
        content: 'Lost touch with Pingwin while it was answering. The answer may still arrive.',
        connectionLost: true,
      }]);
      setLoading(false);
    }
  }, [
    loading,
    pollSessionUntilDone,
    sendChatMessage,
    sessionId,
    setSessionProcessing,
    startPollingGeneration,
  ]);

  const retryLastMessage = useCallback((options?: ChatSendOptions) => {
    const failed = messages[messages.length - 1];
    const question = messages[messages.length - 2];
    if (loading || !failed?.error || question?.role !== 'user') return;
    setMessages(messages.slice(0, -2));
    sendMessage(
      question.content,
      options ?? lastSendOptions.current,
      lastSentFiles.current,
    );
  }, [loading, messages, sendMessage]);

  const renameSession = useCallback(async (
    selectedSessionId: string,
    name: string,
  ): Promise<boolean> => {
    const trimmedName = name.trim();
    if (!trimmedName) return false;

    try {
      const { data } = await dataProvider.update<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        {
          id: selectedSessionId,
          data: { name: trimmedName },
          previousData: { id: selectedSessionId } as ChatSessionDetail,
        },
      );
      if (selectedSessionId === sessionId) setSessionName(data.name || trimmedName);
      return true;
    } catch (e) {
      notify(errorNotification(e, 'Conversation could not be renamed'), { type: 'error' });
      return false;
    }
  }, [dataProvider, notify, sessionId]);

  const archiveSession = useCallback(async (
    selectedSessionId: string,
    isArchived: boolean,
  ): Promise<boolean> => {
    try {
      await dataProvider.update<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        {
          id: selectedSessionId,
          data: { isArchived },
          previousData: { id: selectedSessionId } as ChatSessionDetail,
        },
      );
      return true;
    } catch (e) {
      notify(errorNotification(e, 'Conversation could not be archived'), { type: 'error' });
      return false;
    }
  }, [dataProvider, notify]);

  const cancelSession = useCallback(async (): Promise<boolean> => {
    if (!sessionId || !loading) return false;

    try {
      await dataProvider.update<ChatSessionDetail>(
        resourceNames.PINGU_SESSIONS,
        {
          id: sessionId,
          data: { cancel: true },
          previousData: { id: sessionId } as ChatSessionDetail,
        },
      );
      return true;
    } catch (e) {
      notify(errorNotification(e, 'Conversation could not be stopped'), { type: 'error' });
      return false;
    }
  }, [dataProvider, loading, notify, sessionId]);

  // A failed save is reported on the answer's own feedback row, not as a toast.
  const submitMessageFeedback = useCallback(async (
    messageIndex: number,
    feedback: ChatMessageFeedbackInput,
  ): Promise<boolean> => {
    if (!sessionId) return false;

    try {
      const { data } = await dataProvider.update<ChatSessionDetail>(
        `${resourceNames.PINGU_SESSIONS}/${sessionId}/messages`,
        {
          id: `${messageIndex}/feedback`,
          data: feedback,
          previousData: { id: messageIndex } as unknown as ChatSessionDetail,
        },
      );
      applySession(data);
      return true;
    } catch {
      return false;
    }
  }, [applySession, dataProvider, sessionId]);

  const withdrawMessageFeedback = useCallback(async (messageIndex: number): Promise<boolean> => {
    if (!sessionId) return false;

    try {
      const { data } = await dataProvider.delete<ChatSessionDetail>(
        `${resourceNames.PINGU_SESSIONS}/${sessionId}/messages`,
        {
          id: `${messageIndex}/feedback`,
          previousData: { id: messageIndex } as unknown as ChatSessionDetail,
        },
      );
      applySession(data);
      return true;
    } catch {
      return false;
    }
  }, [applySession, dataProvider, sessionId]);

  return {
    messages,
    sessionId,
    sessionName,
    costBreakdown,
    loading,
    loadingSession,
    sessionProcessingStatuses,
    startingPrompts,
    markMessageTyped,
    archiveSession,
    cancelSession,
    startNewSession,
    loadSession,
    renameSession,
    sendMessage,
    retryLastMessage,
    submitMessageFeedback,
    withdrawMessageFeedback,
  };
};

export type PinguChat = ReturnType<typeof usePinguChat>;

export default usePinguChat;
